import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { getAuthHeaders } from '../context/AuthContext';
import { useFinancial } from '../context/FinancialContext';
import { Lock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FinancialVerifyModal({ onClose }) {
  const { reveal } = useFinancial();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleVerify = async () => {
    if (!password) return;
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/api/admin/verify-password`, { password }, { headers: getAuthHeaders() });
      reveal();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Incorrect password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
    >
      <div className="bg-white rounded-3xl p-7 w-full max-w-sm shadow-2xl" style={{ animation: 'fadeInUp 0.2s ease-out' }}>
        <div className="flex justify-center mb-4">
          <div className="w-12 h-12 bg-[#FFEDD5] rounded-2xl flex items-center justify-center">
            <Lock className="w-6 h-6 text-[#F59E0B]" strokeWidth={1.5} />
          </div>
        </div>
        <h2 className="font-heading text-xl font-semibold text-[#292524] text-center mb-2">Verify Identity</h2>
        <p className="text-sm text-[#57534E] font-body text-center mb-5 leading-relaxed">
          For security, please enter your Admin password to view financial information.
        </p>
        {error && (
          <p className="text-xs text-red-600 font-body bg-red-50 border border-red-200 rounded-xl px-3 py-2 mb-4">{error}</p>
        )}
        <div className="mb-4">
          <label className="block text-sm font-medium text-[#57534E] mb-1.5">Password</label>
          <input
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={e => { setPassword(e.target.value); setError(''); }}
            onKeyDown={e => e.key === 'Enter' && handleVerify()}
            autoFocus
            className="bg-[#FFFAF0] border-2 border-[#E7E5E4] rounded-2xl px-4 py-3 focus:ring-4 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body text-sm"
          />
        </div>
        <div className="flex gap-3">
          <button onClick={onClose}
            className="flex-1 border-2 border-[#E7E5E4] text-[#57534E] rounded-full py-3 font-heading font-medium text-sm hover:border-[#F59E0B] transition-colors">
            Cancel
          </button>
          <button onClick={handleVerify} disabled={loading || !password}
            className="flex-1 bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full py-3 font-heading font-medium text-sm shadow-button disabled:opacity-60 transition-all">
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
